import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { createGlobalStyle } from "styled-components";
import axios from "axios";
import SiteFooter from "../components/SiteFooter";

const Global = createGlobalStyle`
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  body { background: #f0ebe3; font-family: 'Malgun Gothic','Apple SD Gothic Neo',Arial,sans-serif; }
`;

const API_BASE = process.env.REACT_APP_API_URL || "";

/* ── 서비스 정의 ─────────────────────────────────────────────── */
const SERVICES = [
  {
    key: "퀵사주_연애운",
    label: "연애운",
    icon: "💕",
    price: 1000,
    color: "#e03e7c",
    desc: "이상형을 어디서 만날까?\n어떻게 생겼을까? 언제 만날까?",
    needPartner: false,
  },
  {
    key: "퀵사주_직장운",
    label: "직장운",
    icon: "💼",
    price: 1000,
    color: "#2563eb",
    desc: "이직은 언제?\n재직 중이라면 승진할까?",
    needPartner: false,
  },
  {
    key: "퀵사주_재물운",
    label: "재물운",
    icon: "💰",
    price: 1000,
    color: "#059669",
    desc: "투자 성공할까?\n계약운, 사업 매출은?",
    needPartner: false,
  },
  {
    key: "퀵사주_연애궁합",
    label: "연애궁합",
    icon: "💑",
    price: 2000,
    color: "#7c3aed",
    desc: "궁합점수 0~100점\n성격궁합 · 속궁합",
    needPartner: true,
    ageLimit: true,
  },
];

const HOURS = [
  { val: 0,  label: "모름 / 자시이전" },
  { val: 1,  label: "자시 (23~01시)" },
  { val: 2,  label: "축시 (01~03시)" },
  { val: 3,  label: "인시 (03~05시)" },
  { val: 4,  label: "묘시 (05~07시)" },
  { val: 5,  label: "진시 (07~09시)" },
  { val: 6,  label: "사시 (09~11시)" },
  { val: 7,  label: "오시 (11~13시)" },
  { val: 8,  label: "미시 (13~15시)" },
  { val: 9,  label: "신시 (15~17시)" },
  { val: 10, label: "유시 (17~19시)" },
  { val: 11, label: "술시 (19~21시)" },
  { val: 12, label: "해시 (21~23시)" },
];

const CITY_GROUPS = [
  { label: "🇰🇷 국내", cities: ["서울","부산","대구","인천","광주","대전","울산","제주","수원","성남","용인","청주","전주","강릉","포항","창원","고양","춘천","목포","세종"] },
  { label: "🌏 동아시아", cities: ["도쿄","오사카","베이징","상하이","홍콩","타이베이"] },
  { label: "🌏 동남아시아", cities: ["방콕","싱가포르","쿠알라룸푸르","자카르타","마닐라","하노이","호치민"] },
  { label: "🌍 중동·유럽", cities: ["두바이","이스탄불","런던","파리","베를린","모스크바"] },
  { label: "🌎 북미·오세아니아", cities: ["뉴욕","로스앤젤레스","토론토","시드니","멜버른"] },
];

const BIRTH_TYPES = [
  { value: "solar",      label: "양력" },
  { value: "lunar",      label: "음력" },
  { value: "lunar_leap", label: "음력 윤달" },
];

/* ── 공통 인풋 스타일 ─────────────────────────────────────────── */
const inp: React.CSSProperties = {
  width: "100%", padding: "10px 12px",
  border: "1.5px solid #d8cfe8", borderRadius: 10,
  fontSize: "0.9rem", color: "#2d1f3d",
  background: "#faf8fd", fontFamily: "inherit", outline: "none",
};
const lbl: React.CSSProperties = {
  fontSize: 12, color: "#5a4a72", fontWeight: 600,
  display: "block", marginBottom: 4,
};
const field: React.CSSProperties = { flex: 1, display: "flex", flexDirection: "column", gap: 4 };
const row: React.CSSProperties = { display: "flex", gap: 8, marginBottom: 10 };


interface PersonForm {
  name: string;
  gender: string;
  birth_type: string;
  birth_year: string;
  birth_month: string;
  birth_day: string;
  birth_hour: string;
  city: string;
}

const emptyPerson = (): PersonForm => ({
  name: "", gender: "female", birth_type: "solar",
  birth_year: "", birth_month: "", birth_day: "",
  birth_hour: "0", city: "서울",
});

const emptyEmail = () => "";

export default function QuickPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState<0 | 1 | 2 | 3>(0);
  const [svcKey, setSvcKey] = useState<string>("");
  const [person1, setPerson1] = useState<PersonForm>(emptyPerson());
  const [person2, setPerson2] = useState<PersonForm>(emptyPerson());
  const [email, setEmail] = useState<string>(emptyEmail());
  const [emailSent, setEmailSent] = useState(false);
  const [reportHtml, setReportHtml] = useState<string>("");
  const [err, setErr] = useState<string>("");

  const svc = SERVICES.find(s => s.key === svcKey);

  function selectService(key: string) {
    setSvcKey(key);
    setPerson1(emptyPerson());
    setPerson2(emptyPerson());
    setEmail(emptyEmail());
    setEmailSent(false);
    setReportHtml("");
    setErr("");
    setStep(1);
  }

  function validateForm(): string | null {
    const p = person1;
    if (!p.name.trim()) return "이름을 입력해주세요.";
    const yr = parseInt(p.birth_year);
    if (!yr || yr < 1900 || yr > 2020) return "생년을 올바르게 입력해주세요. (1900~2020)";
    const mo = parseInt(p.birth_month);
    if (!mo || mo < 1 || mo > 12) return "생월을 올바르게 입력해주세요. (1~12)";
    const dy = parseInt(p.birth_day);
    if (!dy || dy < 1 || dy > 31) return "생일을 올바르게 입력해주세요. (1~31)";
    if (!email.trim()) return "결과를 받을 이메일 주소를 입력해주세요.";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) return "올바른 이메일 주소를 입력해주세요.";
    if (svc?.needPartner) {
      const p2 = person2;
      if (!p2.name.trim()) return "상대방 이름을 입력해주세요.";
      const yr2 = parseInt(p2.birth_year);
      if (!yr2 || yr2 < 1900 || yr2 > 2020) return "상대방 생년을 올바르게 입력해주세요.";
      const mo2 = parseInt(p2.birth_month);
      if (!mo2 || mo2 < 1 || mo2 > 12) return "상대방 생월을 올바르게 입력해주세요.";
      const dy2 = parseInt(p2.birth_day);
      if (!dy2 || dy2 < 1 || dy2 > 31) return "상대방 생일을 올바르게 입력해주세요.";
      if (svc?.ageLimit) {
        const now = new Date().getFullYear();
        if (now - yr < 20 || now - yr2 < 20) return "연애궁합은 만 20세 이상만 이용 가능합니다.";
      }
    }
    return null;
  }

  function doSubmit() {
    setErr("");
    const e = validateForm();
    if (e) { setErr(e); return; }
    callAnalyze();
  }

  async function callAnalyze() {
    setErr("");
    setStep(2);
    try {
      const body: any = {
        service_type: svcKey,
        email: email.trim(),
        person1: {
          name: person1.name,
          gender: person1.gender,
          birth_year:  parseInt(person1.birth_year),
          birth_month: parseInt(person1.birth_month),
          birth_day:   parseInt(person1.birth_day),
          birth_hour:  parseInt(person1.birth_hour),
          birth_type: person1.birth_type,
          city: person1.city,
        },
      };
      if (svc?.needPartner) {
        body.person2 = {
          name: person2.name,
          gender: person2.gender,
          birth_year:  parseInt(person2.birth_year),
          birth_month: parseInt(person2.birth_month),
          birth_day:   parseInt(person2.birth_day),
          birth_hour:  parseInt(person2.birth_hour),
          birth_type: person2.birth_type,
          city: person2.city,
        };
      }
      const res = await axios.post(`${API_BASE}/api/v1/quick/analyze`, body);
      setEmailSent(res.data.email_sent === true);
      setReportHtml(res.data.report_html || "");
      setStep(3);
    } catch (e: any) {
      setErr(e?.response?.data?.detail || "분석 중 오류가 발생했습니다.");
      setStep(1);
    }
  }

  /* ── 입력 폼 블록 ─────────────────────────────────────────── */
  function PersonFormBlock({
    title, data, onChange, accentColor, showEmail,
  }: { title: string; data: PersonForm; onChange: (d: PersonForm) => void; accentColor: string; showEmail?: boolean }) {
    return (
      <div style={{
        background: "#fff", borderRadius: 18, padding: "20px 20px 14px",
        boxShadow: "0 2px 16px rgba(74,32,96,0.08)",
        border: `1.5px solid ${accentColor}20`,
        marginBottom: 14,
      }}>
        <div style={{ fontSize: 13, fontWeight: 800, color: accentColor, marginBottom: 16,
          display: "flex", alignItems: "center", gap: 6 }}>
          <span style={{ display: "inline-block", width: 4, height: 14,
            background: accentColor, borderRadius: 2 }} />
          {title}
        </div>

        {/* 이름 + 성별 */}
        <div style={row}>
          <div style={field}>
            <label style={lbl}>이름</label>
            <input style={inp} value={data.name} placeholder="홍길동"
              onChange={e => onChange({ ...data, name: e.target.value })} />
          </div>
          <div style={{ ...field, maxWidth: 110 }}>
            <label style={lbl}>성별</label>
            <select style={inp} value={data.gender}
              onChange={e => onChange({ ...data, gender: e.target.value })}>
              <option value="female">여성</option>
              <option value="male">남성</option>
            </select>
          </div>
        </div>

        {/* 양력/음력 */}
        <div style={{ ...row, marginBottom: 10 }}>
          <div style={field}>
            <label style={lbl}>양력 / 음력</label>
            <select style={inp} value={data.birth_type}
              onChange={e => onChange({ ...data, birth_type: e.target.value })}>
              {BIRTH_TYPES.map(bt => (
                <option key={bt.value} value={bt.value}>{bt.label}</option>
              ))}
            </select>
          </div>
        </div>

        {/* 생년 월 일 — 모두 텍스트 인풋 */}
        <div style={row}>
          <div style={field}>
            <label style={lbl}>생년</label>
            <input style={inp} type="number" min={1900} max={2020}
              placeholder="1990" value={data.birth_year}
              onChange={e => onChange({ ...data, birth_year: e.target.value })} />
          </div>
          <div style={{ ...field, maxWidth: 90 }}>
            <label style={lbl}>생월</label>
            <input style={inp} type="number" min={1} max={12}
              placeholder="1" value={data.birth_month}
              onChange={e => onChange({ ...data, birth_month: e.target.value })} />
          </div>
          <div style={{ ...field, maxWidth: 90 }}>
            <label style={lbl}>생일</label>
            <input style={inp} type="number" min={1} max={31}
              placeholder="1" value={data.birth_day}
              onChange={e => onChange({ ...data, birth_day: e.target.value })} />
          </div>
        </div>

        {/* 태어난 시간 */}
        <div style={{ ...row, marginBottom: 10 }}>
          <div style={field}>
            <label style={lbl}>태어난 시간 (모르면 '모름' 선택)</label>
            <select style={inp} value={data.birth_hour}
              onChange={e => onChange({ ...data, birth_hour: e.target.value })}>
              {HOURS.map(h => (
                <option key={h.val} value={h.val}>{h.label}</option>
              ))}
            </select>
          </div>
        </div>

        {/* 출생 지역 */}
        <div style={{ ...row, marginBottom: showEmail ? 10 : 4 }}>
          <div style={field}>
            <label style={lbl}>출생 지역</label>
            <select style={inp} value={data.city}
              onChange={e => onChange({ ...data, city: e.target.value })}>
              {CITY_GROUPS.map(g => (
                <optgroup key={g.label} label={g.label}>
                  {g.cities.map(c => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </optgroup>
              ))}
            </select>
          </div>
        </div>

        {/* 이메일 (나의 정보에만 표시) */}
        {showEmail && (
          <div style={{ ...row, marginBottom: 4 }}>
            <div style={field}>
              <label style={lbl}>결과 받을 이메일</label>
              <input
                style={inp} type="email" placeholder="example@email.com"
                value={email}
                onChange={e => setEmail(e.target.value)}
              />
              <div style={{ fontSize: 11, color: "#9a8aaa", marginTop: 3 }}>
                입력하신 이메일로 결과지가 발송됩니다
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  const accentColor = svc?.color || "#7048a8";

  return (
    <>
      <Global />
      <div style={{ maxWidth: 560, margin: "0 auto", padding: "0 0 80px" }}>

        {/* ── 상단 네비게이션 바 ── */}
        <div style={{
          display: "flex", alignItems: "center", padding: "16px 16px 0",
          marginBottom: 4,
        }}>
          <button
            onClick={() => navigate("/")}
            style={{
              display: "flex", alignItems: "center", gap: 6,
              background: "rgba(255,255,255,0.7)", border: "1.5px solid #d8cfe8",
              borderRadius: 20, padding: "7px 14px", cursor: "pointer",
              fontSize: 13, fontWeight: 700, color: "#5a4a72",
              fontFamily: "inherit", boxShadow: "0 2px 8px rgba(74,32,96,0.08)",
            }}>
            ← 홈
          </button>
          {step === 1 && (
            <button
              onClick={() => { setStep(0); setErr(""); }}
              style={{
                display: "flex", alignItems: "center", gap: 6,
                background: "rgba(255,255,255,0.7)", border: "1.5px solid #d8cfe8",
                borderRadius: 20, padding: "7px 14px", cursor: "pointer",
                fontSize: 13, fontWeight: 700, color: "#5a4a72",
                fontFamily: "inherit", boxShadow: "0 2px 8px rgba(74,32,96,0.08)",
                marginLeft: 8,
              }}>
              ↩ 서비스 선택
            </button>
          )}
          <div style={{ flex: 1 }} />
          <div style={{ fontSize: 15, fontWeight: 900, color: "#4a2060" }}>🍪 쿠키사주</div>
        </div>

        {/* ── 헤더 ── */}
        <div style={{ textAlign: "center", padding: "20px 16px 24px" }}>
          <div style={{ fontSize: "2rem", marginBottom: 6 }}>🍪</div>
          <div style={{ fontSize: "1.4rem", fontWeight: 900, color: "#2d1f3d" }}>쿠키사주</div>
          <div style={{ fontSize: "0.8rem", color: "#7a6a8a", marginTop: 4 }}>
            궁금한 운세 하나를 딱 골라 명리학으로 풀어드립니다
          </div>
        </div>

        <div style={{ padding: "0 16px" }}>

          {/* ── STEP 0: 서비스 선택 ── */}
          {step === 0 && (
            <>
              <div style={{ fontSize: 13, fontWeight: 700, color: "#7a6a8a", textAlign: "center", marginBottom: 16 }}>
                궁금한 운세를 선택하세요
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                {SERVICES.map(s => (
                  <button
                    key={s.key}
                    onClick={() => selectService(s.key)}
                    style={{
                      background: "#fff", border: `2px solid ${s.color}28`,
                      borderRadius: 18, padding: "20px 16px",
                      cursor: "pointer", textAlign: "left",
                      boxShadow: "0 4px 16px rgba(74,32,96,0.07)",
                      fontFamily: "inherit", position: "relative", overflow: "hidden",
                      transition: "transform 0.15s, box-shadow 0.15s",
                    }}
                    onMouseEnter={e => {
                      e.currentTarget.style.transform = "translateY(-3px)";
                      e.currentTarget.style.boxShadow = `0 8px 28px ${s.color}22`;
                    }}
                    onMouseLeave={e => {
                      e.currentTarget.style.transform = "none";
                      e.currentTarget.style.boxShadow = "0 4px 16px rgba(74,32,96,0.07)";
                    }}
                  >
                    <div style={{
                      position: "absolute", top: 0, left: 0, right: 0, height: 3,
                      background: s.color, borderRadius: "18px 18px 0 0",
                    }} />
                    <div style={{ fontSize: "1.8rem", marginBottom: 8 }}>{s.icon}</div>
                    <div style={{ fontSize: 14, fontWeight: 900, color: s.color, marginBottom: 6 }}>{s.label}</div>
                    <div style={{ fontSize: 11, color: "#7a6a8a", lineHeight: 1.65, whiteSpace: "pre-line", marginBottom: 12 }}>
                      {s.desc}
                    </div>
                    <div style={{
                      display: "inline-block", background: `${s.color}14`,
                      color: s.color, borderRadius: 20, padding: "5px 12px",
                      fontSize: 11, fontWeight: 700,
                    }}>
                      {s.price.toLocaleString()}원 →
                    </div>
                  </button>
                ))}
              </div>
            </>
          )}

          {/* ── STEP 1: 입력 폼 ── */}
          {step === 1 && svc && (
            <>
              {/* 선택된 서비스 배지 */}
              <div style={{
                display: "flex", alignItems: "center", gap: 10,
                background: `${svc.color}10`, border: `1.5px solid ${svc.color}30`,
                borderRadius: 14, padding: "12px 16px", marginBottom: 18,
              }}>
                <span style={{ fontSize: "1.5rem" }}>{svc.icon}</span>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 900, color: svc.color, fontSize: 14 }}>
                    쿠키사주 {svc.label} — {svc.price.toLocaleString()}원
                  </div>
                  {svc.ageLimit && (
                    <div style={{ fontSize: 11, color: "#e03131", fontWeight: 700, marginTop: 2 }}>
                      ※ 만 20세 이상만 이용 가능
                    </div>
                  )}
                </div>
              </div>

              <PersonFormBlock
                title="내 정보"
                data={person1}
                onChange={setPerson1}
                accentColor={svc.color}
                showEmail={true}
              />

              {svc.needPartner && (
                <>
                  <div style={{
                    textAlign: "center", fontSize: 12, color: "#9a8aaa",
                    margin: "4px 0 12px", fontWeight: 600,
                  }}>
                    ↕ 상대방 정보도 입력해주세요
                  </div>
                  <PersonFormBlock
                    title="상대방 정보"
                    data={person2}
                    onChange={setPerson2}
                    accentColor={svc.color}
                  />
                </>
              )}

              {err && (
                <div style={{
                  padding: "10px 14px", background: "#fff5f8",
                  border: "1px solid #f5c2cf", borderRadius: 10,
                  fontSize: 13, color: "#c0392b", marginBottom: 14,
                }}>
                  {err}
                </div>
              )}

              <button
                onClick={doSubmit}
                style={{
                  width: "100%", padding: "15px",
                  background: `linear-gradient(135deg,${svc.color},${svc.color}cc)`,
                  color: "#fff", border: "none", borderRadius: 14,
                  fontSize: "1rem", fontWeight: 800, cursor: "pointer",
                  fontFamily: "inherit",
                  boxShadow: `0 4px 18px ${svc.color}44`,
                }}>
                🔮 &nbsp;{svc.label} 바로 확인하기
              </button>
              <div style={{ fontSize: 11, color: "#9a8aaa", textAlign: "center", marginTop: 10 }}>
                확인 즉시 화면에 결과가 표시됩니다
              </div>
            </>
          )}

          {/* ── STEP 2: 분석 중 ── */}
          {step === 2 && (
            <div style={{ textAlign: "center", padding: "60px 0" }}>
              <div style={{ fontSize: "2.5rem", marginBottom: 16 }}>🍪</div>
              <div style={{ fontSize: 16, fontWeight: 800, color: "#2d1f3d", marginBottom: 8 }}>
                사주 분석 중...
              </div>
              <div style={{ fontSize: 13, color: "#7a6a8a" }}>
                명리학으로 운세를 풀고 있습니다. 잠시만 기다려주세요.
              </div>
            </div>
          )}

          {/* ── STEP 3: 결과 ── */}
          {step === 3 && svc && (
            <>
              {/* 이메일 발송 알림 배너 */}
              {emailSent && (
                <div style={{
                  display: "flex", alignItems: "center", gap: 10,
                  background: `${svc.color}12`, border: `1.5px solid ${svc.color}35`,
                  borderRadius: 14, padding: "12px 16px", marginBottom: 14,
                }}>
                  <span style={{ fontSize: "1.3rem" }}>📩</span>
                  <div style={{ fontSize: 13, color: "#2d1f3d", lineHeight: 1.6 }}>
                    <strong style={{ color: svc.color }}>{email}</strong>으로도 발송되었습니다.<br />
                    <span style={{ fontSize: 11, color: "#9a8aaa" }}>메일이 안 보이면 스팸 폴더를 확인하세요.</span>
                  </div>
                </div>
              )}

              {/* 결과 HTML */}
              {reportHtml ? (
                <iframe
                  srcDoc={reportHtml}
                  title="쿠키사주 결과"
                  style={{
                    width: "100%", border: "none", borderRadius: 16,
                    minHeight: 700, display: "block",
                    boxShadow: "0 4px 24px rgba(74,32,96,0.1)",
                    marginBottom: 16,
                  }}
                  onLoad={(e) => {
                    const iframe = e.currentTarget;
                    try {
                      const h = iframe.contentDocument?.documentElement?.scrollHeight;
                      if (h && h > 100) iframe.style.height = h + 40 + "px";
                    } catch { /* cross-origin 무시 */ }
                  }}
                />
              ) : (
                <div style={{
                  background: "#fff", borderRadius: 18, padding: "36px 24px",
                  textAlign: "center", boxShadow: "0 4px 24px rgba(74,32,96,0.1)",
                  marginBottom: 16,
                }}>
                  <div style={{ fontSize: "2rem", marginBottom: 12 }}>🍪</div>
                  <div style={{ fontSize: 15, fontWeight: 800, color: "#2d1f3d", marginBottom: 8 }}>
                    분석이 완료되었습니다!
                  </div>
                  <div style={{ fontSize: 13, color: "#7a6a8a" }}>
                    결과를 준비 중입니다. 잠시 후 다시 확인해주세요.
                  </div>
                </div>
              )}

              {/* 하단 버튼 */}
              <div style={{ display: "flex", gap: 10, marginBottom: 20 }}>
                <button
                  onClick={() => {
                    setStep(0); setSvcKey(""); setPerson1(emptyPerson());
                    setPerson2(emptyPerson()); setEmail(emptyEmail());
                    setEmailSent(false); setReportHtml("");
                  }}
                  style={{
                    flex: 1, padding: "13px",
                    background: "#fff", border: "1.5px solid #d8cfe8",
                    borderRadius: 14, fontSize: 14, fontWeight: 700,
                    color: "#5a4a72", cursor: "pointer", fontFamily: "inherit",
                  }}>
                  다른 운세 보기
                </button>
                <button
                  onClick={() => navigate("/client")}
                  style={{
                    flex: 1, padding: "13px",
                    background: `linear-gradient(135deg,${svc.color},${svc.color}cc)`,
                    border: "none", borderRadius: 14, fontSize: 14, fontWeight: 800,
                    color: "#fff", cursor: "pointer", fontFamily: "inherit",
                  }}>
                  상세 사주 보기
                </button>
              </div>

            </>
          )}
        </div>
        {(step === 0 || step === 3) && <SiteFooter />}
      </div>
    </>
  );
}
